import React, { useState } from "react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import AdminApiTable from "@/modules/racket_lab_admin/AdminApiTable";
import ConfirmActionDialog from "@/modules/racket_lab_admin/ConfirmActionDialog";
import RLOperatorTensionPresetDialog from "@/modules/racket_lab_admin/RLOperatorTensionPresetDialog";
import RLResnapshotCard from "@/modules/racket_lab_admin/RLResnapshotCard";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminOperators — Sub-sprint B.
 * List + invite + toggle status. No hard-delete in UI (§3 decision).
 *
 * QUICK 1.D i18n migration: page header, counter, form placeholders,
 * columns, status badge, toggle dialog title/description/confirmLabel
 * and submit button resolved via `racketLab.operators.*`. The
 * `status` payload values (`active`/`inactive`) stay unchanged for
 * routing + `data-testid`; their visible labels come from
 * `operators.status_active/inactive`.
 */
const RLAdminOperators = () => {
  const t = useRacketLabAdminT();
  // RACKET-LAB-ROLE-4I (Feb 2026) — admin/staff-only filter on the
  // computed `user_link_status` flag. The backend ignores this query
  // param for non-admin viewers (computed flag is admin-only).
  const [linkStatusFilter, setLinkStatusFilter] = useState("");
  const { data, loading, error, reload } = useAdminApi(
    "/admin/racket-lab/operators",
    {
      params: linkStatusFilter
        ? { user_link_status: linkStatusFilter }
        : undefined,
    },
  );
  const { run, loading: mutating } = useAdminMutation();
  const [form, setForm] = useState({ user_id: "", display_label: "", organisation_id: "" });
  // RACKET-LAB-ROLE-4 (Feb 2026) — modal state for the per-operator
  // tension-preset dialog. Admin/staff can save & clear; other profiles
  // see the read-only view.
  const [presetOp, setPresetOp] = useState(null);

  const rows = Array.isArray(data) ? data : data?.items || data?.operators || [];

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.organisation_id) return;
    await run("post", "/admin/racket-lab/operators", { ...form, assigned_shop_ids: [] }).catch(() => {});
    setForm({ user_id: "", display_label: "", organisation_id: "" });
    reload();
  };

  const toggleStatus = (op) => async () => {
    const next = op.status === "active" ? "inactive" : "active";
    await run("post", `/admin/racket-lab/operators/${op.id}/status`, { status: next }).catch(() => {});
    reload();
  };

  const statusLabel = (s) =>
    s === "active" ? t("operators.status_active")
    : s === "inactive" ? t("operators.status_inactive")
    : (s || "—");

  const columns = [
    { key: "display_label", label: t("operators.columns.label"), render: (r) => (
      <span data-testid={`rl-op-label-${r.id}`} className="font-medium">{r.display_label || "—"}</span>
    )},
    { key: "user_id", label: t("operators.columns.user"), render: (r) => (
      <code className="text-[10px] text-slate-500">{r.user_id?.slice(0, 18) || "—"}</code>
    )},
    { key: "status", label: t("operators.columns.status"), render: (r) => (
      <span data-testid={`rl-op-status-${r.id}`} className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
        r.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"
      }`}>{statusLabel(r.status)}</span>
    )},
    // RACKET-LAB-ROLE-4I (Feb 2026) — admin/staff-only computed badge.
    // The backend omits the field for non-admin viewers, so we render
    // a "—" placeholder there to keep the column shape stable.
    { key: "user_link_status", label: t("operators.columns.user_link_status"), render: (r) => {
      const v = r.user_link_status;
      if (!v) {
        return <span data-testid={`rl-op-link-status-${r.id}`} className="text-[10px] text-slate-300">—</span>;
      }
      const tone =
        v === "linked"
          ? "bg-emerald-100 text-emerald-800"
          : v === "orphan_review"
            ? "bg-amber-100 text-amber-800"
            : "bg-slate-100 text-slate-600";
      return (
        <span
          data-testid={`rl-op-link-status-${r.id}`}
          className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${tone}`}
        >
          {t(`operators.user_link_status.${v}`)}
        </span>
      );
    }},
    { key: "tension_preset", label: t("operators.columns.tension_preset"), render: (r) => {
      const hasPreset = r.default_tension_unit != null || r.default_main_tension != null || r.default_cross_tension != null;
      return (
        <button
          type="button"
          data-testid={`rl-op-tension-preset-btn-${r.id}`}
          onClick={() => setPresetOp(r)}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-slate-50"
        >
          {hasPreset ? (
            <span data-testid={`rl-op-tension-preset-summary-${r.id}`}>
              {r.default_main_tension ?? "—"}/{r.default_cross_tension ?? "—"} {r.default_tension_unit ?? ""}
            </span>
          ) : (
            <span data-testid={`rl-op-tension-preset-empty-${r.id}`}>{t("operators.tension_preset.summary_empty")}</span>
          )}
        </button>
      );
    }},
    { key: "actions", label: t("operators.columns.actions"), render: (r) => (
      <ConfirmActionDialog
        title={t("operators.toggle_title")}
        description={t("operators.toggle_description", {
          label: r.display_label || r.id,
          from: statusLabel(r.status),
          to: statusLabel(r.status === "active" ? "inactive" : "active"),
        })}
        confirmLabel={t("operators.toggle_confirm_label")}
        onConfirm={toggleStatus(r)}
        disabled={mutating}
        dataTestId={`rl-op-toggle-${r.id}`}
        trigger={
          <button type="button" className="rounded-md bg-slate-100 px-2 py-1 text-xs hover:bg-slate-200">
            {t("operators.toggle_btn")}
          </button>
        }
      />
    )},
  ];

  return (
    <div data-testid="rl-admin-operators-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />
      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold text-slate-900">{t("operators.page_header")}</h1>
        <p className="text-xs text-slate-500">
          {t(rows.length === 1 ? "operators.counter" : "operators.counter_plural", { count: rows.length })}
        </p>
      </header>

      <form onSubmit={onCreate} data-testid="rl-op-create-form" className="rounded-md border border-slate-200 bg-white p-3 flex flex-wrap gap-2 text-xs">
        <input data-testid="rl-op-input-user-id" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}
          placeholder={t("operators.placeholder_user_id")} className="flex-1 min-w-[160px] rounded border border-slate-300 px-2 py-1.5" />
        <input data-testid="rl-op-input-label" value={form.display_label} onChange={(e) => setForm({ ...form, display_label: e.target.value })}
          placeholder={t("operators.placeholder_label")} className="flex-1 min-w-[140px] rounded border border-slate-300 px-2 py-1.5" />
        <input data-testid="rl-op-input-org" value={form.organisation_id} onChange={(e) => setForm({ ...form, organisation_id: e.target.value })}
          placeholder={t("operators.placeholder_org")} className="flex-1 min-w-[160px] rounded border border-slate-300 px-2 py-1.5" />
        <button type="submit" disabled={mutating} data-testid="rl-op-submit"
          className="rounded-md bg-emerald-600 px-3 py-1.5 text-white hover:bg-emerald-700 disabled:bg-emerald-300">
          {t("operators.invite_submit")}
        </button>
      </form>

      <AdminApiTable columns={columns} rows={rows} loading={loading} error={error}
        emptyLabel={t("operators.empty")} dataTestId="rl-operators-table" />

      {/* RACKET-LAB-ROLE-4I (Feb 2026) — admin/staff-only filter on the
          computed `user_link_status` flag. Backend silently ignores the
          query param for non-admin viewers; the select therefore has no
          effect outside admin/staff even if rendered. Kept under the
          table so it does not push the create form down. */}
      <div className="flex items-center gap-2 text-xs text-slate-600">
        <label htmlFor="rl-op-link-status-filter">
          {t("operators.user_link_status.filter_label")}
        </label>
        <select
          id="rl-op-link-status-filter"
          data-testid="rl-op-link-status-filter"
          value={linkStatusFilter}
          onChange={(e) => setLinkStatusFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-2 py-1"
        >
          <option value="">{t("operators.user_link_status.filter_all")}</option>
          <option value="linked">{t("operators.user_link_status.linked")}</option>
          <option value="orphan_review">{t("operators.user_link_status.orphan_review")}</option>
          <option value="orphan_safe">{t("operators.user_link_status.orphan_safe")}</option>
        </select>
      </div>

      {/* RACKET-LAB-ROLE-4F (Feb 2026) — admin/staff-only console for
          the ROLE-4E re-snapshot endpoint. Rendered after the operators
          table so it lives in the same operational surface but does
          not interfere with the existing list-and-toggle flows. */}
      <RLResnapshotCard />

      <RLOperatorTensionPresetDialog
        operator={presetOp}
        open={!!presetOp}
        onClose={() => setPresetOp(null)}
        onSaved={() => { setPresetOp(null); reload(); }}
      />
    </div>
  );
};

export default RLAdminOperators;
